import React, { useState, useRef, useEffect } from "react";
import { cn } from "../../../utils/util";
import styles from "./datepicker.module.css";
import { Calendar, ChevronLeft, ChevronRight, X, ChevronDown } from "lucide-react";
import { Field } from "formik";
import { useTranslation } from "react-i18next";

/**
 * DatePicker component for selecting dates
 * Uses Formik's Field component internally
 * 
 * @param {Object} props
 * @param {string} props.name - Field name (required for Formik)
 * @param {string} props.label - Label for the date picker
 * @param {string} props.placeholder - Placeholder text when no date is selected
 * @param {string} props.format - Date format (default: DD/MM/YYYY)
 * @param {boolean} props.disabled - Whether the date picker is disabled
 * @param {string} props.className - Additional CSS class
 * @param {Object} props.style - Additional inline styles
 * @param {boolean} props.required - Whether the field is required
 * @param {Date} props.minDate - Minimum selectable date
 * @param {Date} props.maxDate - Maximum selectable date
 * @param {Function} props.validate - Custom validation function
 */

const DatePicker = ({
  
  name,
  label,
  placeholder = "เลือกวันที่",
  format = "DD/MM/YYYY",
  disabled = false,
  className,
  style,
  required = false,
  minDate,
  maxDate,
  validate,
  ...props
}) => {
  
  if (!name) {
    console.error("DatePicker requires a name prop for Formik integration");
    return null;
  }

  return (
    <Field name={name} validate={validate}>
      {({ field, form }) => (
        <DatePickerInner
          field={field}
          form={form}
          name={name}
          label={label}
          placeholder={placeholder}
          format={format}
          disabled={disabled}
          className={className}
          style={style}
          required={required}
          minDate={minDate}
          maxDate={maxDate}
          {...props}
        />
      )}
    </Field>
  );
};

// Internal component that handles the date picker logic
const DatePickerInner = ({
  field,
  form,
  name,
  label,
  placeholder = "เลือกวันที่",
  format = "DD/MM/YYYY",
  disabled = false,
  className,
  style,
  required = false,
  minDate,
  maxDate,
  ...props
}) => {
  const { t } = useTranslation("register");
  placeholder = t("selectD");
  const fieldError = form.errors[name];
  const fieldTouched = form.touched[name];
  const showError = fieldError && (fieldTouched || form.submitCount > 0);

  // Convert string date to Date object if needed
  const parseDate = (dateValue) => {
    if (!dateValue) return null;
    if (dateValue instanceof Date) return dateValue;
    
    // If it's a string, parse it based on expected format
    if (typeof dateValue === 'string') {
      // Try parsing as ISO string first
      const date = new Date(dateValue);
      if (!isNaN(date.getTime())) return date;
      
      // Otherwise parse based on format
      const parts = dateValue.split(/[-\/]/);
      if (parts.length !== 3) return null;
      
      let day, month, year;
      switch (format) {
        case "DD/MM/YYYY":
          [day, month, year] = parts;
          break;
        case "MM/DD/YYYY":
          [month, day, year] = parts;
          break;
        case "YYYY-MM-DD":
          [year, month, day] = parts;
          break;
        default:
          [day, month, year] = parts;
      }
      
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }
    
    return null;
  };

  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(parseDate(field.value));
  const [currentMonth, setCurrentMonth] = useState(selectedDate || new Date());
  const [showYearSelector, setShowYearSelector] = useState(false);
  const datePickerRef = useRef(null);

  // Generate a range of years for the year selector
  const currentYear = new Date().getFullYear();
  const yearRange = Array.from({ length: 100 }, (_, i) => currentYear - 80 + i);

  // Close date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowYearSelector(false);
        // Trigger onBlur for Formik validation
        form.setFieldTouched(name, true);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [form, name]);

  useEffect(() => {
    if (field.value) {
      setSelectedDate(parseDate(field.value));
    } else {
      setSelectedDate(null);
    }
  }, [field.value]);

  // Format date to display
  const formatDate = (date) => {
    if (!date) return "";
    
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    
    // Handle different date formats
    switch (format) {
      case "DD/MM/YYYY":
        return `${day}/${month}/${year}`;
      case "MM/DD/YYYY":
        return `${month}/${day}/${year}`;
      case "YYYY-MM-DD":
        return `${year}-${month}-${day}`;
      default:
        return `${day}/${month}/${year}`;
    }
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const days = [];
    const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const lastDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    
    // Start from the first day of the week containing the first day of the month
    const startDate = new Date(firstDayOfMonth);
    startDate.setDate(startDate.getDate() - firstDayOfMonth.getDay());
    
    // End with the last day of the week containing the last day of the month
    const endDate = new Date(lastDayOfMonth);
    if (endDate.getDay() < 6) {
      endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));
    }
    
    // Generate all days in the calendar view
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      days.push({
        date: new Date(currentDate),
        isCurrentMonth: currentDate.getMonth() === currentMonth.getMonth()
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return days;
  };

  // Check if a date is disabled
  const isDateDisabled = (date) => {
    if (disabled) {
      return true;
    }

    if (minDate && date < new Date(minDate.setHours(0, 0, 0, 0))) {
      return true;
    }

    if (maxDate && date > new Date(maxDate.setHours(23, 59, 59, 999))) {
      return true;
    }

    return false;
  };

  const handleDateSelect = (date) => {
    if (disabled) return;
    
    if (isDateDisabled(date)) {
      return;
    }
    
    setSelectedDate(date);
    setIsOpen(false);
    setShowYearSelector(false);
    
    // Update Formik field value
    const formattedDate = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`;
    form.setFieldValue(name, formattedDate);
    
    // Mark as touched for validation
    form.setFieldTouched(name, true);
  };

  const handleMonthChange = (increment) => {
    if (disabled) return;
    
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + increment);
    setCurrentMonth(newMonth);
  };

  const handleYearSelect = (year) => {
    if (disabled) return;
    
    const newDate = new Date(currentMonth);
    newDate.setFullYear(year);
    setCurrentMonth(newDate);
    setShowYearSelector(false);
  };

  const toggleCalendar = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      setShowYearSelector(false);
    }
  };

  const toggleYearSelector = (e) => {
    if (disabled) return;
    
    e.stopPropagation();
    setShowYearSelector(!showYearSelector);
  };

  const clearDate = (e) => {
    if (disabled) return;
    
    e.stopPropagation();
    setSelectedDate(null);
    
    // Update Formik field value
    form.setFieldValue(name, "");
    // Mark as touched for validation
    form.setFieldTouched(name, true);
  };

  // Get Thai month names
  const getThaiMonthName = (month) => {
    const thaiMonths = [
      t("jan"), t("feb"), t("mar"), t("apr"), t("may"), t("jun"),
      t("jul"), t("aug"), t("sep"), t("oct"), t("nov"), t("dec")
    ];

    return thaiMonths[month];
  };

  // Get week day names in Thai
  const weekDays = [
    t("sun"), t("mon"), t("tue"), t("wed"),
    t("thu"), t("fri"), t("sat")
  ];
  return (
    <div className={cn(styles.datePickerContainer, className)} style={style} ref={datePickerRef}>
      {label && (
        <label className={styles.label} htmlFor={name}>
          {label}
          {required && <span className={styles.required}> *</span>}
        </label>
      )}
      
      <div 
        id={name}
        className={cn(
          styles.datePickerInput, 
          disabled && styles.disabled,
          showError && styles.error
        )}
        onClick={disabled ? undefined : toggleCalendar}
        aria-disabled={disabled}
        {...field}
        {...props}
        value={undefined} // Don't pass value to div
        onChange={undefined} // Don't pass onChange to div
      >
        <div className={styles.inputValue}>
          {selectedDate ? formatDate(selectedDate) : (
            <span className={styles.placeholder}>{placeholder}</span>
          )}
        </div>
        
        <div className={styles.inputActions}>
          {selectedDate && !disabled && (
            <button 
              type="button" 
              className={styles.clearButton}
              onClick={clearDate}
              aria-label="Clear date"
              disabled={disabled}
            >
              <X size={16} />
            </button>
          )}
          <div className={cn(styles.calendarIcon, disabled && styles.disabledIcon)}>
            <Calendar size={20} />
          </div>
        </div>
      </div>
      
      {showError && (
        <div className={styles.errorMessage}>{fieldError}</div>
      )}
      
      {isOpen && !disabled && (
        <div className={styles.calendar}>
          <div className={styles.calendarHeader}>
            <button 
              type="button"
              className={styles.navButton}
              onClick={() => handleMonthChange(-1)}
              aria-label="Previous month"
            >
              <ChevronLeft size={16} />
            </button>
            
            <div 
              className={styles.monthYearSelector} 
              onClick={toggleYearSelector}
            >
              <div className={styles.currentMonth}>
                {getThaiMonthName(currentMonth.getMonth())} {currentMonth.getFullYear()}
              </div>
              <ChevronDown size={16} className={cn(styles.yearSelectorIcon, showYearSelector && styles.yearSelectorIconOpen)} />
              
              {showYearSelector && (
                <div className={styles.yearSelector}>
                  {yearRange.map(year => (
                    <button
                      key={year}
                      type="button"
                      onClick={() => handleYearSelect(year)}
                      className={cn(
                        styles.yearOption,
                        currentMonth.getFullYear() === year && styles.selectedYear
                      )}
                    >
                      {year}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <button 
              type="button"
              className={styles.navButton}
              onClick={() => handleMonthChange(1)}
              aria-label="Next month"
            >
              <ChevronRight size={16} />
            </button>
          </div>
          
          <div className={styles.weekDays}>
            {weekDays.map((day, index) => (
              <div key={`weekday-${index}`} className={styles.weekDay}>
                {day}
              </div>
            ))}
          </div>
          
          <div className={styles.days}>
            {generateCalendarDays().map((dayInfo, index) => {
              const { date, isCurrentMonth } = dayInfo;
              const isSelected = selectedDate && 
                date.getDate() === selectedDate.getDate() && 
                date.getMonth() === selectedDate.getMonth() && 
                date.getFullYear() === selectedDate.getFullYear();
              
              const isToday = 
                date.getDate() === new Date().getDate() && 
                date.getMonth() === new Date().getMonth() && 
                date.getFullYear() === new Date().getFullYear();
              
              const disabled = isDateDisabled(date);
              
              return (
                <button
                  type="button"
                  key={`day-${index}`}
                  className={cn(
                    styles.day,
                    isSelected && styles.selected,
                    !isCurrentMonth && styles.otherMonth,
                    isToday && styles.today,
                    disabled && styles.disabled
                  )}
                  onClick={() => handleDateSelect(date)}
                  disabled={disabled}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePicker;